import * as React from 'react';
import TableBody from '@mui/material/TableBody';
import { v4 as uuidv4 } from 'uuid';
import TableCell from '@mui/material/TableCell';
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import TableRow from '@mui/material/TableRow';
import { filter } from 'lodash';
import { FilterUploadHistory, FinalDataUploadHistory, HistoryItem } from '../../../../../interfaces/interfaces';
import tableHeadCellNames, { filterUploadHistoryKeys } from '../uploadHistory.mock';
import uploadHistoryHandler from '../../../../../utils/adapters/uploadHistoryHandler';
import uploadHistoryAdapter from '../../../../../utils/adapters/uploadHistoryAdapter';
import adaptString from '../../../../../utils/adapters/stringAdapter';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledTableCell = styled(TableCell)(() => ({
  overflowWrap: 'break-word',
}));

const StyledURL = styled('a')({
  color: '#4b286d',
  textDecoration: 'none',

  '&:hover': {
    color: '#092c4c',
  },
});

interface TableBodyUploadHistoryProps {
  loading: boolean | undefined;
  tablePage: number;
  data: HistoryItem[] | undefined;
  filterData: FilterUploadHistory;

  setTablePage(x: number): void;
  setAmountPage(x: number): void;
}

const TableBodyUploadHistory = ({ loading, tablePage, filterData, data = [], setAmountPage, setTablePage }: TableBodyUploadHistoryProps) => {
  const [newData, setNewData] = React.useState<FinalDataUploadHistory[] | []>([]);
  const [filterNewData, setFilterNewData] = React.useState<FinalDataUploadHistory[] | []>([]);

  React.useEffect(() => {
    setNewData(uploadHistoryAdapter(data, uploadHistoryHandler));
  }, [data]);

  React.useEffect(() => {
    setFilterNewData(newData);
  }, [newData]);

  React.useEffect(() => {
    try {
      setTablePage(1);
      setFilterNewData(filter(newData, (item) => filterUploadHistoryKeys.every((i) => adaptString(item?.[i]).includes(filterData?.[i]))));
    } catch (e: any) {
      console.error(e); // eslint-disable-line no-console
    }
  }, [filterData]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => setAmountPage(filterNewData.length), [filterNewData, setAmountPage]);

  const dataLimitPerTablePage = 10;
  const getPaginationData = React.useCallback(
    (historyData: FinalDataUploadHistory[] = []) => {
      const startIndex = tablePage * dataLimitPerTablePage - dataLimitPerTablePage;
      const endIndex = startIndex + dataLimitPerTablePage;
      return historyData?.slice(startIndex, endIndex);
    },
    [tablePage]
  );

  const skeletonArrayRow = Array(10).fill('');
  const skeletonArrayCell = Array(Object.values(tableHeadCellNames).length).fill('');

  return (
    <TableBody>
      {loading
        ? skeletonArrayRow.map(() => (
            <StyledTableRow key={uuidv4()}>
              {skeletonArrayCell.map(() => (
                <TableCell key={uuidv4()}>
                  <Skeleton animation="wave" />
                </TableCell>
              ))}
            </StyledTableRow>
          ))
        : getPaginationData(filterNewData)?.map((i: FinalDataUploadHistory) => (
            <StyledTableRow key={uuidv4()}>
              <StyledTableCell>{i.uploadedOn}</StyledTableCell>
              <StyledTableCell>{i.uploadedBy}</StyledTableCell>
              <StyledTableCell>{i.fileType}</StyledTableCell>
              <StyledTableCell>{i.fileContent}</StyledTableCell>
              <StyledTableCell>{i.fileSource}</StyledTableCell>
              <StyledTableCell>{i.version}</StyledTableCell>
              <StyledTableCell>{i.fileLastUpdated}</StyledTableCell>
              <StyledTableCell>
                <StyledURL href={i.fileDataUrl} rel="noopener" target="_blank">
                  {i.fileName}
                </StyledURL>
              </StyledTableCell>
              <StyledTableCell>{i.uploadStatus}</StyledTableCell>
              <StyledTableCell>
                <Button variant="contained" fullWidth>
                  Report
                </Button>
              </StyledTableCell>
            </StyledTableRow>
          ))}
    </TableBody>
  );
};

export default TableBodyUploadHistory;
